import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Mail, Phone, MessageCircle, MessageSquareText, Send } from "lucide-react";
import { toast } from "sonner";

export function normalizePhone(phone) {
  if (!phone) return "";
  let p = phone.trim().replace(/[\s-]/g, "");
  if (p.startsWith("+")) return p;
  if (p.startsWith("0")) return "+250" + p.slice(1);
  if (p.startsWith("250")) return "+" + p;
  return "+250" + p;
}

export function ContactIcons({ email, phone, userId, name, isGuest, setMessageTarget }) {
  return (
    <div className="flex items-center gap-2 mt-0.5">
      {email && setMessageTarget && (
        <button type="button" onClick={() => setMessageTarget({ id: userId, name, isGuest: !!isGuest })} className="text-accent" title={`Message ${email}`}>
          <Mail size={12} />
        </button>
      )}
      {phone && <a href={`tel:${normalizePhone(phone)}`} className="text-accent" title={phone}><Phone size={12} /></a>}
      {phone && <a href={`https://wa.me/${normalizePhone(phone).replace("+", "")}`} target="_blank" rel="noreferrer" style={{ color: "#25D366" }} title="WhatsApp"><MessageCircle size={12} /></a>}
      {phone && <span className="text-muted-foreground/40 cursor-not-allowed" title="SMS — coming soon"><MessageSquareText size={12} /></span>}
    </div>
  );
}

export function MessageComposeDialog({ messageTarget, setMessageTarget }) {
  const [messageSubject, setMessageSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const close = () => { setMessageTarget(null); setMessageSubject(""); setMessageBody(""); };

  const sendMessage = async () => {
    if (messageSubject.trim().length < 2 || messageBody.trim().length < 3) {
      toast.error("Please fill in a subject and message");
      return;
    }
    setSendingMessage(true);
    try {
      const { data } = await api.post(`/admin/message-user/${messageTarget.id}`, {
        subject: messageSubject.trim(),
        message: messageBody.trim(),
      });
      toast.success(data.is_guest ? "Email sent" : "Email and notification sent");
      close();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <Dialog open={Boolean(messageTarget)} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="admin-message-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Message {messageTarget?.name}</DialogTitle>
          <DialogDescription>
            {messageTarget?.isGuest
              ? "Sent by email only — this is a guest booking with no account."
              : "Sent by email and as an in-app notification."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Subject</Label>
            <Input className="mt-1.5" value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)} placeholder="About your booking" data-testid="admin-message-subject" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Message</Label>
            <Textarea rows={5} className="mt-1.5" value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Write your message here..." data-testid="admin-message-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={sendMessage} disabled={sendingMessage} className="bg-primary hover:bg-primary/90" data-testid="admin-message-send">
            <Send size={14} className="mr-1.5" /> {sendingMessage ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
